/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from "react";

import {
  MenuItem,
  Grid,
  IconButton,
  Modal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  SelectChangeEvent,
} from "@mui/material";
import { Close, ExpandMore } from "@mui/icons-material";
import { Skeleton } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import Button, { Color } from "../../../../../../../../components/Form/Button";
import Checkbox from "../../../../../../../../components/Form/Checkbox";
import Input from "../../../../../../../../components/Form/Input";
import Select from "../../../../../../../../components/Form/Select";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import { NotificationToast } from "../../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../../context/StateContext";
import useActionSyncUser from "../../../../../../../../hooks/useSyncUser";
import { Error } from "../../../../../../../../models/error";
import { Resources } from "../../../../../../../../models/resources";
import { Roles } from "../../../../../../../../models/user";
import ResourceService from "../../../../../../../../services/resource.service";
import RoleService from "../../../../../../../../services/role.service";
import { checkError } from "../../../../../../../../util/checkError";
import { Status } from "../../../../../../../../util/enums";
import useStyles from "./styles";

type Option = {
  value: string;
  name: string;
};

type RoleResource = {
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
  id: number;
};

type ResourceGroups = {
  [x: string]: Resources[];
};

interface Props {
  roleToBeEditId?: number;
  isViewRole?: boolean;
  isOpen: boolean;
  closeModal: () => void;
  refreshList: () => void;
}

export const CreateRoleModal = ({ roleToBeEditId, isOpen, closeModal, refreshList }: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const { mutate: syncUser } = useActionSyncUser();

  const [resources, setResources] = useState<ResourceGroups>({});

  const [roleName, setRoleName] = useState<string>("");
  const [roleStatus, setRoleStatus] = useState<string>("");
  const [roleDescription, setRoleDescription] = useState<string>("");
  const [roleResources, setRoleResources] = useState<RoleResource[]>([]);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [responseError, setResponseError] = useState<Error>();
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const [roleToBeEdit, setRoleToBeEdit] = useState<Roles>();

  const handleSyncUser = (roleId: number) => {
    if (roleId && roleId === currentUser?.roleId) {
      syncUser();
    }
  };

  const schema = yup.object().shape({
    roleName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    roleStatus: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    roleDescription: yup.string(),
  });

  const userStatus: Option[] = useMemo(
    () => [
      { value: Status.ACTIVE, name: Status.ACTIVE },
      { value: Status.INACTIVE, name: Status.INACTIVE },
    ],
    []
  );

  const fieldHasError = (field: string) => !!errors?.find((error: any) => error.path === field);
  const getErrorByField = (field: string) =>
    errors?.find((error: any) => error.path === field)?.message;

  const isValid = (role: any) => {
    try {
      schema.validateSync(
        {
          roleName: role.name,
          roleStatus: role.status,
          roleDescription: role.description,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleChangeResourceCheckbox = (
    id: number,
    attribute: "add" | "edit" | "view" | "delete",
    value: boolean
  ) => {
    const arrRoleResource = roleResources;

    if (arrRoleResource.length === 0)
      arrRoleResource.push({
        id,
        view: attribute === "view" ? value : false,
        edit: attribute === "edit" ? value : false,
        add: attribute === "add" ? value : false,
        delete: attribute === "delete" ? value : false,
      });
    else {
      const findRoleResource = arrRoleResource
        .map((resource, index: number) => (resource.id === id ? { data: resource, index } : false))
        .filter((item) => typeof item !== "boolean")[0];

      if (findRoleResource && typeof findRoleResource !== "boolean") {
        const { data, index } = findRoleResource;

        data[attribute] = value;
        arrRoleResource[index] = data;
      } else {
        arrRoleResource.push({
          id,
          view: attribute === "view" ? value : false,
          edit: attribute === "edit" ? value : false,
          add: attribute === "add" ? value : false,
          delete: attribute === "delete" ? value : false,
        });
      }
    }

    setRoleResources([...arrRoleResource]);
  };

  const handleSetRoleResourceCheckbox = (
    id: number,
    attribute: "add" | "edit" | "view" | "delete"
  ): boolean => {
    const findRoleResource = roleResources.find((resource) => resource.id === id);

    if (findRoleResource) return findRoleResource[attribute];

    return false;
  };

  const handleCleanFields = () => {
    setRoleName("");
    setRoleStatus("");
    setRoleDescription("");
    setRoleResources([]);
  };

  const { isFetching } = useQuery(
    ["resource.list"],
    () =>
      ResourceService.getAll({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          const resourceGroups: ResourceGroups = {};

          response.data.forEach((resource) => {
            const keys = Object.keys(resourceGroups);

            if (keys.length === 0 || !keys.includes(resource.group))
              resourceGroups[resource.group] = [resource];
            else if (keys.includes(resource.group)) resourceGroups[resource.group].push(resource);
          });

          setResources(resourceGroups);
        }
      },
    }
  );

  const { refetch, isFetching: isFetchingRole } = useQuery(
    ["role", roleToBeEditId],
    () => RoleService.getById(roleToBeEditId || 0),
    {
      enabled: !!roleToBeEditId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setRoleToBeEdit(response.data);
          setRoleName(response.data.name);
          setRoleStatus(response.data.status);
          setRoleDescription(response.data.description);
          if (response.data.resources.length > 0) {
            response.data.resources.forEach((resource: Resources) => {
              if (resource.roles_resources) {
                const newRole = {
                  id: resource.roles_resources.resourceId,
                  add: resource.roles_resources.add,
                  delete: resource.roles_resources.delete,
                  edit: resource.roles_resources.edit,
                  view: resource.roles_resources.view,
                };

                setRoleResources((state) => state && [...state, { ...newRole }]);
              }
            });
          }
        }
      },
    }
  );

  const { isLoading, mutate: createRole } = useMutation(
    ["role.create"],
    (role: any) => RoleService.create(role),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        handleCleanFields();
        refreshList();
        closeModal();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) {
          if (error.response.data.field !== "unknown") setResponseError(error.response.data);
          else checkError(error, translate("anErrorOccurredTryAgain"));
        }
      },
    }
  );

  const { isLoading: isLoadingUpdatingRole, mutate: updateRole } = useMutation(
    ["role.update"],
    ({ id, role }: { id: number; role: any }) => RoleService.update(id, role),
    {
      onSuccess: (_, variables) => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        handleCleanFields();
        refreshList();
        closeModal();
        handleSyncUser(variables?.id);
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) {
          if (error.response.data.field !== "unknown") setResponseError(error.response.data);
          else checkError(error, translate("anErrorOccurredTryAgain"));
        }
      },
    }
  );

  const handleSubmit = () => {
    let object = {};

    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (roleToBeEditId) {
      object = {
        ...roleToBeEdit,
        name: roleName,
        status: roleStatus,
        description: roleDescription,
        resources: [...roleResources],
      };
      if (isValid(object)) updateRole({ id: roleToBeEditId, role: object });
    } else {
      object = {
        companyId: currentUser?.companyId,
        name: roleName,
        status: roleStatus,
        description: roleDescription,
        type: "Custom",
        resources: [...roleResources],
      };

      if (isValid(object)) createRole(object);
    }
  };

  useEffect(() => {
    if (roleToBeEditId) {
      refetch();
    }
  }, [roleToBeEditId]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid({
        name: roleName,
        status: roleStatus,
        description: roleDescription,
      });
    }
  }, [roleName, roleStatus, roleDescription, hasFormBeenSubmitted]);

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        closeModal();
        handleCleanFields();
      }}
    >
      <div data-cy="modalToCreateOrEditRole" className={classes.paper}>
        <div className={classes.modalBody}>
          <header className={classes.modalHeader}>
            <div>
              <p data-cy="modalToCreateOrEditRoleTile" className={classes.modalHeaderTitle}>
                {roleToBeEdit ? translate("editRole") : translate("createRole")}
              </p>
              <span>{roleToBeEdit ? translate("editThisRole") : translate("createNewRole")}</span>
            </div>
            <IconButton onClick={closeModal}>
              <Close />
            </IconButton>
          </header>
          <section className={classes.modalSection}>
            <Grid container direction="row" alignItems="flex-start" spacing={2}>
              <Grid item md={6} lg={6}>
                <SkeletonComponent isLoading={isFetchingRole}>
                  <Input
                    disabled={isLoading}
                    name="roleName"
                    label={translate("roleName")}
                    placeholder="Admin"
                    value={roleName}
                    error={fieldHasError("roleName")}
                    errorMessage={getErrorByField("roleName")}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (responseError) setResponseError(undefined);
                      setRoleName(e.target.value);
                    }}
                    errorResponse={responseError}
                  />
                </SkeletonComponent>
              </Grid>
              <Grid item md={6} lg={6}>
                <SkeletonComponent isLoading={isFetchingRole}>
                  <Select
                    tabIndex={0}
                    disabled={isLoading}
                    name="status"
                    data-cy="roleStatus"
                    label={translate("status")}
                    error={fieldHasError("roleStatus")}
                    errorMessage={getErrorByField("roleStatus")}
                    value={roleStatus}
                    onChange={(e: SelectChangeEvent<any>) => setRoleStatus(e.target.value)}
                  >
                    {userStatus.map((status) => (
                      <MenuItem key={status.name} value={status.value} data-cy={status.name}>
                        {status.name}
                      </MenuItem>
                    ))}
                  </Select>
                </SkeletonComponent>
              </Grid>
              <Grid item md={12} lg={12}>
                <SkeletonComponent isLoading={isFetchingRole}>
                  <Input
                    disabled={isLoading}
                    name="description"
                    label={translate("roleDescriptionOptional")}
                    error={fieldHasError("roleDescription")}
                    errorMessage={getErrorByField("roleDescription")}
                    placeholder="Admin"
                    value={roleDescription}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setRoleDescription(e.target.value)
                    }
                  />
                </SkeletonComponent>
              </Grid>
            </Grid>
            <section className={`mt-3 ${classes.permissionsList}`}>
              <p className={classes.modalBodyTitle}>{translate("setPermissions")}</p>
              <Grid
                container
                direction="row"
                alignItems="flex-start"
                spacing={1}
                className={classes.accordionDetailsGrid}
              >
                {isFetching &&
                  new Array(6).fill("undefined").map((_, index: number) => (
                    <Grid item md={6} lg={6} key={index}>
                      <Skeleton className={classes.skeleton} />
                    </Grid>
                  ))}
                {Object.keys(resources)
                  ?.sort()
                  ?.map((group) => (
                    <Grid item md={6} lg={6} key={group}>
                      <Accordion className={classes.accordion}>
                        <AccordionSummary
                          data-cy={`accordion${group}`}
                          className={classes.accordionSummary}
                          expandIcon={<ExpandMore />}
                          aria-controls="accordionSummary"
                          id={group}
                        >
                          <span className={classes.accordionDetailsTitle}>{group}</span>
                        </AccordionSummary>
                        <AccordionDetails data-cy={`accordionDetails${group}`}>
                          <Grid container direction="column" alignItems="flex-end" spacing={2}>
                            {resources[group]
                              .sort((resourceA, resourceB) =>
                                resourceA.name.localeCompare(resourceB.name)
                              )
                              .map((resource: Resources, index: number) => (
                                <Grid
                                  item
                                  container
                                  direction="row"
                                  alignItems="center"
                                  key={index}
                                >
                                  <Grid item md={3} lg={3}>
                                    <span
                                      className={`
                                      ${classes.accordionDetailsTitle}
                                      ${classes.accordionDetailsLabel}
                                    `}
                                    >
                                      {resource.name === group
                                        ? group.toLowerCase() !== "settings"
                                          ? "Page"
                                          : resource.name
                                        : resource.name}
                                    </span>
                                  </Grid>
                                  <Grid item container md={9} lg={9} spacing={2}>
                                    <Grid item md={4} lg={4}>
                                      <Checkbox
                                        control={{
                                          "data-cy": resource.name,
                                        }}
                                        label={"View"}
                                        checked={handleSetRoleResourceCheckbox(
                                          resource?.id || 0,
                                          "view"
                                        )}
                                        onChange={(_, checked) =>
                                          handleChangeResourceCheckbox(
                                            resource?.id || 0,
                                            "view",
                                            checked
                                          )
                                        }
                                      />
                                    </Grid>
                                    <Grid item md={4} lg={4}>
                                      <Checkbox
                                        control={{
                                          "data-cy": resource.name,
                                        }}
                                        label="Add"
                                        checked={handleSetRoleResourceCheckbox(
                                          resource?.id || 0,
                                          "add"
                                        )}
                                        onChange={(_, checked) =>
                                          handleChangeResourceCheckbox(
                                            resource?.id || 0,
                                            "add",
                                            checked
                                          )
                                        }
                                      />
                                    </Grid>
                                    <Grid item md={4} lg={4}>
                                      <Checkbox
                                        control={{
                                          "data-cy": resource.name,
                                        }}
                                        label="Edit"
                                        checked={handleSetRoleResourceCheckbox(
                                          resource?.id || 0,
                                          "edit"
                                        )}
                                        onChange={(_, checked) =>
                                          handleChangeResourceCheckbox(
                                            resource?.id || 0,
                                            "edit",
                                            checked
                                          )
                                        }
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                              ))}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  ))}
              </Grid>
            </section>
          </section>
          <section className={classes.modalFooter}>
            <Button
              data-cy="cancelOperationButton"
              customColor={Color.CANCEL}
              disabled={isLoading}
              onClick={() => {
                closeModal();
                handleCleanFields();
              }}
            >
              {translate("cancel")}
            </Button>
            <Button
              data-cy="createOrUpdateRoleButton"
              customColor={Color.SUCCESS}
              onClick={handleSubmit}
              loading={isLoading || isLoadingUpdatingRole}
            >
              {roleToBeEditId ? translate("update") : translate("create")}
            </Button>
          </section>
        </div>
      </div>
    </Modal>
  );
};
